import React, { useState, useEffect, useCallback } from 'react';
import { Table, Select, Button, Space, Spin, Empty, Tooltip, Tabs, Tag } from 'antd';
import { ReloadOutlined, BarChartOutlined, UnorderedListOutlined } from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../i18n/format';
import { avecLibelleTraduit } from '../../i18n/libelles';
import TableExportButton from '../../components/TableExportButton';
import PageHeader from '../../components/PageHeader';
import logoLocal from '../../assets/images/logo.png';

const ANNEES = [
    ...avecLibelleTraduit([{ value: 0 }], 'statistiques:litiges.exercices'),
    ...Array.from({ length: 6 }, (_, i) => {
        const a = new Date().getFullYear() - i;
        return { value: a, label: String(a) };
    }),
];

// ── Helpers ────────────────────────────────────────────────────────────────
const fNb = (v) => (v ?? 0).toLocaleString(localeIntl());
const fPct = (v) => v == null ? '-' : `${v.toFixed(1)} %`;

const STATUT_CFG = avecLibelleTraduit({
    CONTENTIEUX: { color: '#991b1b', bg: '#fef2f2' },
    ARBITRAGE:   { color: '#92400e', bg: '#fffbeb' },
}, 'statistiques:litiges.statuts');

const StatutTag = ({ statut }) => {
    const cfg = STATUT_CFG[statut] || { color: '#374151', bg: '#f3f4f6', label: statut };
    return (
        <Tag style={{
            color: cfg.color, background: cfg.bg,
            border: `1px solid ${cfg.color}30`,
            borderRadius: 6, fontWeight: 700, fontSize: 11,
        }}>
            {cfg.label}
        </Tag>
    );
};

const TypeTag = ({ type }) => {
    const isET = type === 'SURVENU_TOGO';
    return (
        <Tag style={{
            color: isET ? '#1e40af' : '#166534',
            background: isET ? '#eff6ff' : '#f0fdf4',
            border: `1px solid ${isET ? '#93c5fd' : '#86efac'}`,
            borderRadius: 6, fontWeight: 600, fontSize: 11,
        }}>
            {isET ? 'ET' : 'TE'}
        </Tag>
    );
};

// ── Colonnes · Récapitulatif par pays ──────────────────────────────────────
const colsRecap = (t) => [
    {
        title: t('litiges.colonnes.paysPartenaire'), dataIndex: 'bureau', key: 'bureau', width: 180, fixed: 'left',
        render: (v, r) => (
            <span style={{
                fontWeight: r.bureau === 'TOTAL' ? 800 : 600,
                color: r.bureau === 'TOTAL' ? '#1a6b3c' : '#111827',
            }}>{v?.toUpperCase()}</span>
        ),
    },
    {
        title: t('litiges.contentieux'), dataIndex: 'nbContentieux', key: 'nbC', align: 'right', width: 130,
        render: (v, r) => (
            <span style={{ fontWeight: r.bureau === 'TOTAL' ? 700 : 400, color: '#991b1b' }}>
                {fNb(v)}
            </span>
        ),
        numeric: true,
        export: (v) => String(v ?? 0),
    },
    {
        title: t('litiges.arbitrage'), dataIndex: 'nbArbitrage', key: 'nbA', align: 'right', width: 120,
        render: (v, r) => (
            <span style={{ fontWeight: r.bureau === 'TOTAL' ? 700 : 400, color: '#92400e' }}>
                {fNb(v)}
            </span>
        ),
        numeric: true,
        export: (v) => String(v ?? 0),
    },
    {
        title: t('litiges.totalEnLitige'), dataIndex: 'nbTotal', key: 'nbT', align: 'right', width: 130,
        render: (v, r) => (
            <span style={{
                fontWeight: r.bureau === 'TOTAL' ? 800 : 700,
                color: r.bureau === 'TOTAL' ? '#1a6b3c' : '#111827',
            }}>{fNb(v)}</span>
        ),
        numeric: true,
        export: (v) => String(v ?? 0),
    },
    {
        title: '% Total', dataIndex: 'pourcentage', key: 'pct', align: 'right', width: 100,
        render: (v) => v == null ? '-' : (
            <span style={{ fontWeight: 600, color: '#374151' }}>{fPct(v)}</span>
        ),
        export: (v) => v == null ? '' : `${v.toFixed(1)} %`,
    },
];

// ── Colonnes · Dossiers en litige ──────────────────────────────────────────
const colsDossiers = (t) => [
    {
        title: t('litiges.colonnes.numeroSinistre'), dataIndex: 'numero', key: 'num', width: 160, fixed: 'left',
        render: (v) => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 600, color: '#1a6b3c' }}>{v}</span>,
    },
    {
        title: t('litiges.colonnes.dateDeclaration'), dataIndex: 'dateDeclaration', key: 'datDecl', width: 110, align: 'center',
    },
    {
        title: t('litiges.colonnes.assure'), dataIndex: 'assure', key: 'assure',
        render: (v) => <span style={{ fontWeight: 500 }}>{v || '-'}</span>,
    },
    {
        title: t('litiges.colonnes.type'), dataIndex: 'typeSinistre', key: 'type', width: 70, align: 'center',
        render: (v) => <TypeTag type={v} />,
        export: (v) => v === 'SURVENU_TOGO' ? 'ET' : 'TE',
    },
    {
        title: t('litiges.colonnes.paysPartenaire'), dataIndex: 'paysPartenaire', key: 'pays', width: 160,
        render: (v) => <span style={{ fontWeight: 500 }}>{v}</span>,
    },
    {
        title: t('litiges.colonnes.statut'), dataIndex: 'statut', key: 'statut', width: 120, align: 'center',
        render: (v) => <StatutTag statut={v} />,
        export: (v) => STATUT_CFG[v]?.label || v,
    },
    {
        title: t('litiges.colonnes.juridiction'), dataIndex: 'niveauJuridiction', key: 'jur',
        render: (v) => v || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>-</span>,
    },
    {
        title: t('litiges.colonnes.prochaineAudience'), dataIndex: 'dateProchaineAudience', key: 'audience', width: 150, align: 'center',
        render: (v) => v
            ? <span style={{ fontWeight: 600, color: '#b45309' }}>{v}</span>
            : <span style={{ color: '#9ca3af' }}>-</span>,
    },
];

// ════════════════════════════════════════════════════════════════════════════
//  COMPOSANT
// ════════════════════════════════════════════════════════════════════════════
export default function EtatLitiges() {
    const { t } = useTranslation('statistiques');
    const [onglet, setOnglet] = useState('recap');
    const [annee, setAnnee] = useState(0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/v1/stats/litiges?annee=${annee}`);
            setData(res.data?.data);
        } catch {
            toast.error(t('litiges.erreurChargement'));
        } finally {
            setLoading(false);
        }
    }, [annee, t]);

    useEffect(() => { load(); }, [load]);

    const libAnnee = annee === 0 ? t('litiges.tousExercices') : String(annee);

    // ── Données tableau récapitulatif ──
    const recapData = data
        ? [
            ...(data.lignes || []).map((r, i) => ({ ...r, key: i })),
            { ...(data.total || {}), key: 'total', _isTotal: true },
        ]
        : [];

    // ── Données liste dossiers ──
    const dossiersData = (data?.dossiers || []).map((d, i) => ({ ...d, key: i }));

    const anneeLabel = annee === 0 ? t('litiges.tousExercices') : annee;
    const exportTitleRecap = t('litiges.export.titreRecap', { annee: anneeLabel });
    const exportTitleDossiers = t('litiges.export.titreDossiers', { annee: anneeLabel });

    const totalEnLitige = data?.total?.nbTotal ?? 0;
    const totalContentieux = data?.total?.nbContentieux ?? 0;
    const totalArbitrage = data?.total?.nbArbitrage ?? 0;

    return (
        <Layout title={t('litiges.titre')}>
            <div>

                {/* Compte : la liste nominative des dossiers (`data.dossiers`), pas
                    `recapData` qui agrege par pays et porte en plus une ligne TOTAL. */}
                <PageHeader
                    titre={t('litiges.titre')}
                    icone={<BarChartOutlined />}
                    compte={data ? dossiersData.length : undefined}
                    unite={t('litiges.unite')}
                    precision={data?.dateArrete ? t('litiges.situationAu', { date: data.dateArrete }) : undefined}
                    actions={(
                        <Space>
                            <Select
                                value={annee}
                                onChange={setAnnee}
                                style={{ width: 180, borderRadius: 8 }}
                                options={ANNEES}
                            />
                            <Tooltip title={t('commun.actualiser')}>
                                <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 8 }} />
                            </Tooltip>
                        </Space>
                    )}
                />

                {/* Compteurs synthèse */}
                {data && (
                    <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                        {[
                            { label: t('litiges.totalEnLitige'), value: totalEnLitige, color: '#1a6b3c', bg: '#f0faf4' },
                            { label: t('litiges.contentieux'), value: totalContentieux, color: '#991b1b', bg: '#fef2f2' },
                            { label: t('litiges.arbitrage'), value: totalArbitrage, color: '#92400e', bg: '#fffbeb' },
                        ].map(({ label, value, color, bg }) => (
                            <div key={label} className="cb-card" style={{
                                padding: '16px 24px', background: bg,
                                border: `1px solid ${color}20`, minWidth: 160, flex: 1,
                            }}>
                                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                                <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1.2, marginTop: 4 }}>{fNb(value)}</div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Onglets */}
                {/* ONGLETS CONTROLES, et non plus `defaultActiveKey` seul. Chaque volet
                    porte son propre export ; pour n'en afficher qu'un au coin bas-droite,
                    il faut savoir lequel est actif - AntD garde montes les volets deja
                    visites, donc deux boutons flottants s'y seraient superposes. */}
                <Tabs
                    activeKey={onglet}
                    onChange={setOnglet}
                    items={[
                        {
                            key: 'recap',
                            label: (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <BarChartOutlined /> {t('litiges.ongletRecap')}
                                </span>
                            ),
                            children: (
                                <div className="cb-card" style={{ padding: 0 }}>
                                    <div style={{
                                        padding: '14px 20px 10px',
                                        borderBottom: '1px solid #f3f4f6',
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    }}>
                                        <span style={{ fontWeight: 700, color: '#1a6b3c', fontSize: 14 }}>
                                            {t('litiges.titreRecap', { annee: libAnnee.toUpperCase() })}
                                        </span>
                                        {onglet === 'recap' && (
                                          <TableExportButton
                                              floating
                                              data={recapData}
                                              columns={colsRecap(t)}
                                              filename={`etat-litiges-recap-${annee || 'tous'}`}
                                              title={exportTitleRecap}
                                              subtitle={t('litiges.export.sousTitreRecap', { annee: libAnnee, date: data?.dateArrete || '-' })}
                                              logo={logoLocal}
                                              showTotals={false}
                                              disabled={!data || recapData.length <= 1}
                                          />
                                        )}
                                    </div>
                                    <Spin spinning={loading}>
                                        {!loading && recapData.length <= 1 ? (
                                            <Empty description={t('litiges.aucunSinistre')} style={{ padding: 40 }} />
                                        ) : (
                                            <Table
                                                className="cb-table"
                                                dataSource={recapData}
                                                columns={colsRecap(t)}
                                                pagination={false}
                                                size="small"
                                                scroll={{ x: 'max-content' }}
                                                rowClassName={(r) => r._isTotal ? 'ant-table-row-total' : ''}
                                            />
                                        )}
                                    </Spin>
                                </div>
                            ),
                        },
                        {
                            key: 'dossiers',
                            label: (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <UnorderedListOutlined /> {t('litiges.ongletDossiers')}
                                    {totalEnLitige > 0 && (
                                        <span style={{
                                            background: '#991b1b', color: '#fff',
                                            borderRadius: 10, fontSize: 11, fontWeight: 700,
                                            padding: '1px 7px', marginLeft: 2,
                                        }}>{totalEnLitige}</span>
                                    )}
                                </span>
                            ),
                            children: (
                                <div className="cb-card" style={{ padding: 0 }}>
                                    <div style={{
                                        padding: '14px 20px 10px',
                                        borderBottom: '1px solid #f3f4f6',
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    }}>
                                        <span style={{ fontWeight: 700, color: '#1a6b3c', fontSize: 14 }}>
                                            {t('litiges.titreDossiers', { annee: libAnnee.toUpperCase() })}
                                        </span>
                                        {onglet === 'dossiers' && (
                                          <TableExportButton
                                              floating
                                              data={dossiersData}
                                              columns={colsDossiers(t)}
                                              filename={`etat-litiges-dossiers-${annee || 'tous'}`}
                                              title={exportTitleDossiers}
                                              subtitle={t('litiges.export.sousTitreDossiers', { annee: libAnnee, date: data?.dateArrete || '-' })}
                                              logo={logoLocal}
                                              disabled={!data || dossiersData.length === 0}
                                          />
                                        )}
                                    </div>
                                    <Spin spinning={loading}>
                                        {!loading && dossiersData.length === 0 ? (
                                            <Empty description={t('litiges.aucunDossier')} style={{ padding: 40 }} />
                                        ) : (
                                            <Table
                                                className="cb-table"
                                                dataSource={dossiersData}
                                                columns={colsDossiers(t)}
                                                pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (n) => t('litiges.nbDossiers', { count: n }) }}
                                                size="small"
                                                scroll={{ x: 'max-content' }}
                                                rowClassName={(r) => r.statut === 'CONTENTIEUX' ? 'row-contentieux' : 'row-arbitrage'}
                                            />
                                        )}
                                    </Spin>
                                </div>
                            ),
                        },
                    ]}
                />

                {/* Source */}
                {data && (
                    <div style={{ marginTop: 12, fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>
                        {t('commun.sourcePointGenereLe', { date: new Date().toLocaleDateString(localeIntl()) })}
                    </div>
                )}
            </div>

            <style>{`
                .ant-table-row-total td { background: #f0faf4 !important; font-weight: 700 !important; }
                .ant-table-row-total:hover td { background: #dcfce7 !important; }
                .row-contentieux td { background: #fff9f9 !important; }
                .row-contentieux:hover td { background: #fef2f2 !important; }
                .row-arbitrage td { background: #fffdf5 !important; }
                .row-arbitrage:hover td { background: #fffbeb !important; }
            `}</style>
        </Layout>
    );
}
